import {
  onTimeFormatChangePlugin,
  onOpenAnnotation,
  onAddingAnnotationDataChanged,
  onAnnotationClickOpened,
  onAnnotationClickClosed,
  onAnnotationClickOpenedV2,
  onAnnotationClickClosedV2,
  onAnnotationsStateChanged,
  onDeleteCommentAnnotation,
  onAnnotationDirtyStateChanged,
  onAnnotationClickedIn,
  onAnnotationMarkerSelectedActiveHover,
  onAnnotationMarkerSelectedInActiveHover,
} from '../../services/annotation.service';
import IntervalTree from '@flatten-js/interval-tree';
import { VideoDrawingHandler } from './modules/video-drawing-handler';
import { BaseDrawingHandlerParams } from '../base-class-handler/base-drawing-handler';
import { BaseClassHandler } from '../base-class-handler/base-class-handler';
import { BaseDraggableMarkerComponent } from '../base-draggable-marker/base-draggable-marker.component';
import { EmitterService } from '../../services/emitter.service';
import { AnnotationHighlightSelectedAnnotation } from '../../interfaces/annotation.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AiOptions, AnnotationParams, ExistingAnnotationData } from '@mtm/conversation-client-sdk-annotation';
import { Canvas } from 'fabric';
import videojs from 'video.js';

export class VideoAnnotationHandler implements BaseClassHandler {
  player: any;
  annotationPlugin: any;
  instanceId: string;
  canvasContainer: any;
  bounds: any;
  active: boolean;
  resizeObserver: ResizeObserver;
  isOldUi: boolean;
  item: any;
  metadata: string;
  videoMetadata: any;
  itemId: string;
  videoDisplay: 'standard' | 'frames';
  intervalTree: IntervalTree<any>;
  visibleAnnotationIds: Set<string> = new Set<string>();
  drawingHandler: VideoDrawingHandler | null = null;
  private animationFrameId: number | null = null;
  private isPlaying: boolean = false;
  private readonly ngUnsubscribe = new Subject<void>();
  private marker: BaseDraggableMarkerComponent | null;
  private markerRangeListenersRegistered = false;
  private hasUnsavedDrawingState = false;
  private channelSubscribed = false;

  get videoJs() {
    return (window as any).videojs;
  }

  get isAdding() {
    return this.annotationPlugin?.controls?.uiState?.adding && this.annotationPlugin?.controls?.uiState?.drawingEnabled;
  }

  get annotatingMode() {
    return this.isAdding || false;
  }

  get editingMode() {
    return this.annotationPlugin?.controls?.uiState?.editing || false;
  }

  get isAnnotatingOrEditing() {
    return this.annotatingMode || this.editingMode;
  }

  get hasUnsavedDrawing(): boolean {
    return this.hasUnsavedDrawingState;
  }

  constructor(player: any, annotationPlugin: any, isOldUi: boolean) {
    window['videoAnnotationHandler'] = this;
    this.player = player;
    this.annotationPlugin = annotationPlugin;
    this.item = this.annotationPlugin.options.item;
    this.metadata = this.item.metadata || '[]';
    this.instanceId = this.annotationPlugin.instanceId;
    this.isOldUi = isOldUi;
    this.itemId = this.item.id;
    this.active = true;
    this.videoDisplay = 'standard';

    this.videoMetadata = JSON.parse(this.metadata).filter((element: any) => {
      if (element.codecType === 'VIDEO') return element;
    })[0];

    player.annotationComments = () => {
      return this;
    };

    onDeleteCommentAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.deleteCommentAnnotation(args),
    });

    if (player.readyState() >= 3) {
      this.setup();
    } else {
      player.one('loadeddata', this.setup.bind(this));
    }
  }

  get annotationMessages(): ExistingAnnotationData[] {
    return this.annotationPlugin?.annotationState?.annotationMessages || [];
  }

  setup(): void {
    const { player } = this;
    player.bigPlayButton?.hide?.();

    const canvasName = 'canvas-fabric-' + this.instanceId;
    this.videoDisplay = 'standard';

    this.setupCanvas(canvasName);
    this.setupUIControls();
    this.bindCanvasResize();
    this.registerListeners();
    this.bindEvents();
    this.setBounds();
  }

  private setupCanvas(canvasName: string): void {
    this.canvasContainer = $('<div>').addClass('vac-canvas');
    $(this.canvasContainer).attr('tabindex', 0);

    const canvasEl = $(`<canvas id="${canvasName}">`).addClass('canvas-free-render');
    this.canvasContainer.append(canvasEl);

    $(this.player.el()).append(this.canvasContainer);

    const canvasFabric = new Canvas(canvasName, {
      isDrawingMode: false,
      selection: false,
      stopContextMenu: true,
    });

    canvasFabric.setHeight(this.player.el().clientHeight);
    canvasFabric.setWidth(this.player.el().clientWidth);

    this.player.canvasFabric = canvasFabric;

    this.initializeDrawingHandler(this.player.canvasFabric);
    this.buildIntervalTree();

    const currentTime = this.player.currentTime();
    const initialAnnotations = this.searchIntervalTree(currentTime);
    this.syncFabricCanvasDrawings(initialAnnotations);

    this.canvasContainer.show();
    this.player.addClass('vac-active');
  }

  private initializeDrawingHandler(canvasFabric: Canvas): void {
    const params: BaseDrawingHandlerParams = {
      canvasFabric: canvasFabric,
      instanceId: this.instanceId,
      annotationPlugin: this.annotationPlugin,
    };

    this.drawingHandler = new VideoDrawingHandler(params, this.player);
  }

  updateCanvasPreview(): void {
    const currentTime = this.player.currentTime();
    const initialAnnotations = this.searchIntervalTree(currentTime);
    this.syncFabricCanvasDrawings(initialAnnotations);
  }

  searchIntervalTree(time: number): any[] {
    const intervals = this.intervalTree.search([time, time]);
    const timeMatched = intervals.map((interval: any) => interval);

    // Include annotations that have no valid start (but not null-range ones, which are handled by the interval tree).
    const nullStartAnnotations =
      this.annotationMessages?.filter((a: any) => {
        const start = a?.range?.start;
        const end = a?.range?.end;
        if (start === null && end === null) {
          return false;
        }
        return typeof start !== 'number' || Number.isNaN(start);
      }) || [];

    const combined = [...timeMatched, ...nullStartAnnotations];
    // Deduplicate by id in case of overlap.
    const seen = new Set<string>();
    return combined.filter((ann: any) => {
      const id = ann?.id;
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  }

  buildIntervalTree() {
    const tree = new IntervalTree();
    const annotations = this.annotationMessages || [];
    const videoDuration = this.player?.duration() || 0;
    annotations.forEach((annotation) => {
      let { start } = annotation.range;
      let { end } = annotation.range;

      if (start === null && end === null) {
        start = 0;
        end = videoDuration;
      }

      if (typeof start !== 'number' || Number.isNaN(start)) {
        return;
      }

      let effectiveEnd;
      if (typeof end !== 'number' || Number.isNaN(end)) {
        effectiveEnd = start + 0.25;
      } else if (end <= 0 || end <= start) {
        effectiveEnd = start + 0.25;
      } else if (start === end || end - start < 0.25) {
        effectiveEnd = start + 0.25;
      } else {
        effectiveEnd = end;
      }

      tree.insert([start, effectiveEnd], annotation);
    });

    this.intervalTree = tree;
  }

  bindEvents() {
    this.player.on('play', () => {
      this.isPlaying = true;
      this.startAnimationLoop();
      this.marker?.onPlay();
    });

    this.player.on('pause', () => {
      this.isPlaying = false;
      this.stopAnimationLoop();
      this.marker?.onBreak();
    });

    this.player.on('ended', () => {
      this.isPlaying = false;
      this.stopAnimationLoop();
      this.marker?.onBreak();
    });

    this.player.on('seeked', () => {
      const time = this.player.currentTime();
      const annotations = this.searchIntervalTree(time);
      this.syncFabricCanvasDrawings(annotations);
      if (!this.marker?.dragging) {
        this.marker?.timeUpdateChange();
      }
    });
  }

  private startAnimationLoop(): void {
    if (this.animationFrameId) {
      this.stopAnimationLoop();
    }

    const animate = () => {
      if (this.isPlaying) {
        const time = this.player.currentTime();
        const annotations = this.searchIntervalTree(time);
        this.syncFabricCanvasDrawings(annotations);
        this.animationFrameId = requestAnimationFrame(animate);
        // const isAnnotating = !!(
        //   this.annotationPlugin?.controls?.uiState?.adding || this.annotationPlugin?.controls?.uiState?.editing
        // );
        // if (isAnnotating && this.marker) {
        //   onAddingAnnotationDataChanged.emit({
        //     range: { start: this.player.currentTime(), end: this.marker.range.end },
        //     itemId: this.itemId,
        //     instanceId: this.instanceId,
        //   });
        // }
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private stopAnimationLoop(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  syncFabricCanvasDrawings(activeAnnotations: any[]) {
    if (!this.drawingHandler) {
      return;
    }
    const nextVisibleIds = new Set(activeAnnotations.map((a) => a.id));

    this.removeStaleAnnotations(nextVisibleIds);

    this.addOrUpdateAnnotations(activeAnnotations);

    this.visibleAnnotationIds = nextVisibleIds;

    if (this.player.canvasFabric) {
      this.player.canvasFabric.renderAll();
    }
  }

  private removeStaleAnnotations(nextVisibleIds: Set<string>): void {
    for (const id of this.visibleAnnotationIds) {
      if (!nextVisibleIds.has(id)) {
        this.drawingHandler?.removeAnnotationDrawings(id);
      }
    }
  }

  private addOrUpdateAnnotations(activeAnnotations: any[]): void {
    for (const annotation of activeAnnotations) {
      const annotationId = annotation?.id;
      if (!annotationId) {
        continue;
      }

      // If an annotation is already visible on canvas, only refresh its opacity
      if (this.visibleAnnotationIds.has(annotationId)) {
        this.updateAnnotationOpacity(annotationId);
        continue;
      }

      const isAnnotationOpen = this.isAnnotationOpen(annotation.id);
      this.drawingHandler?.addAnnotationDrawings(
        annotation.id,
        annotation.shape,
        true,
        !isAnnotationOpen,
        annotation.resolution
      );
      if (isAnnotationOpen) {
        this.drawingHandler?.revertOpacity(annotation.id);
      }
    }
  }

  private updateAnnotationOpacity(annotationId: string): void {
    if (!this.drawingHandler) {
      return;
    }

    const isAnnotationOpen = this.isAnnotationOpen(annotationId);

    if (isAnnotationOpen) {
      this.drawingHandler.revertOpacity(annotationId);
    } else {
      this.drawingHandler.decreaseOpacity(annotationId);
    }
  }

  private isAnnotationOpen(annotationId: string): boolean {
    if (!this.drawingHandler) {
      return false;
    }

    if (this.drawingHandler.clickedAnnotation?.id === annotationId) {
      return true;
    }

    if (this.drawingHandler.hoveredAnnotation?.id === annotationId) {
      return true;
    }

    const pluginActiveAnnotation = this.annotationPlugin?.annotationState?.activeAnnotation;
    if (pluginActiveAnnotation?.id === annotationId) {
      return true;
    }
    if (pluginActiveAnnotation?.data?.id === annotationId) {
      return true;
    }
    if (pluginActiveAnnotation?.annotation?.id === annotationId) {
      return true;
    }

    return false;
  }

  private setupUIControls(): void {
    const toggleOptions = {
      beforeElement: (this.player.options_ as any).plugins.subtitle ? 'subtitleButton' : 'DownloadButton',
    };
    this.bindToggleAnnotDisplay(toggleOptions);
    this.bindTimeFormatControlDisplay();
  }

  registerVjsComponent(name: string): void {
    const Component = videojs.getComponent('Component');
    class componentName extends Component {
      constructor(player: any, options?: any) {
        super(player, options);
      }
    }
    videojs.registerComponent(name, componentName);
  }

  bindToggleAnnotDisplay(options: { beforeElement?: string }): void {
    const { player } = this;

    this.registerVjsComponent('ToggleAnnotDisplay');
    const TDButton = this.player.controlBar.addChild('ToggleAnnotDisplay', {});
    const TDElement = TDButton?.el?.();
    if (!TDElement) {
      return;
    }
    TDElement.innerHTML = `<img alt="" src="../../../../../assets/img/annotations/toggleDisplayIconEnabled.png" height="38" width="38">`;
    TDElement.classList.add('vac-toggle-annot-display');
    TDElement.title = 'Show / hide annotations';
    TDElement.id = 'toggleDisplayIcon';

    if (options.beforeElement) {
      const beforeElement = player.controlBar.getChild(options.beforeElement);
      if (beforeElement) {
        player.controlBar.el().insertBefore(TDElement, beforeElement.el());
      }
    }

    $(document).on('click', '#toggleDisplayIcon', () => {
      this.toggleAnnotationMode();
    });
  }

  bindTimeFormatControlDisplay(): void {
    const _this = this;
    const { player } = this;
    if (!player?.controlBar?.durationDisplay) {
      return;
    }
    const buttonComp = player.controlBar.durationDisplay.addChild('component', {});
    const buttonEl = buttonComp.el();
    buttonEl.innerHTML = '<i class="fa fa-play"></i>';
    buttonEl.classList.add('caret', 'px-1');

    this.registerVjsComponent('TimeFormatDropdown');
    const dropDownComp = player.controlBar.durationDisplay.addChild('TimeFormatDropdown', {});
    const dropDownEl = dropDownComp.el();
    dropDownEl.innerHTML = `
            <div class="std-format pt-1 active">
                <span>Seconds</span>
            </div>
            <div class="frame-format pt-1">
                <span>Frames</span>
            </div>`;
    dropDownEl.classList.add('format-types');

    const controlTimeEls = document.querySelectorAll('.vjs-time-control, .vjs-duration-display .format-types');
    const controlTimeCaretEl = document.querySelector('.vjs-duration .caret') as HTMLElement | null;

    controlTimeEls.forEach((el) => {
      const $el = $(el);
      $el.on('click', function () {
        if (controlTimeCaretEl) {
          $(controlTimeCaretEl).toggleClass('active');
        }
        controlTimeEls.forEach((subEl) => {
          $(subEl).toggleClass('active');
        });
      });
    });

    const stdControlBarEl = document.querySelector('.vjs-duration-display .std-format');
    const frameControlBarEl = document.querySelector('.vjs-duration-display .frame-format');

    $(stdControlBarEl as HTMLElement).on('click', function () {
      _this.changeTimeFormat('standard');
      $(this).toggleClass('active');
      $(frameControlBarEl as HTMLElement).removeClass('active');
    });

    $(frameControlBarEl as HTMLElement).on('click', function () {
      _this.changeTimeFormat('frames');
      $(this).toggleClass('active');
      $(stdControlBarEl as HTMLElement).removeClass('active');
    });

    controlTimeEls.forEach((el) => {
      (el as HTMLElement).title = 'Time Format';
    });
    const formatTypesEl = document.querySelector('.vjs-duration-display .format-types') as HTMLElement | null;
    if (formatTypesEl) {
      formatTypesEl.title = '';
    }
  }

  bindCanvasResize(): void {
    const element = this.player.el();
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === element) {
          this.setBounds();
        }
      }
    });
    this.resizeObserver.observe(element);
  }

  setBounds(triggerChange: boolean = true): void {
    this.bounds = {};
    const $player = $(this.player.el());
    const $ctrls = $player.find('.vjs-control-bar');

    const offset = $player.offset();
    this.bounds.left = offset?.left ?? 0;
    this.bounds.top = offset?.top ?? 0;
    this.bounds.right = this.bounds.left + ($player.width() ?? 0);
    this.bounds.bottom = this.bounds.top + ($player.height() ?? 0);
    this.bounds.bottomWithoutControls = this.bounds.bottom - ($ctrls.height() ?? 0);

    const { canvasFabric } = this.player;

    if (this.isOldUi) {
      const outerCanvasContainer = this.player.el();
      const ratio = canvasFabric.getWidth() / canvasFabric.getHeight();
      const containerWidth = outerCanvasContainer.clientWidth;
      const scale = containerWidth / canvasFabric.getWidth();
      canvasFabric.setDimensions({ width: containerWidth, height: containerWidth / ratio });
      this.rescaleCanvas(scale);
    } else {
      const videoElement = this.player.el().querySelector('.vjs-tech') as HTMLVideoElement | null;

      if (!videoElement) {
        return;
      }
      const displayHeight = videoElement.offsetHeight;
      const displayWidth = videoElement.offsetWidth;
      const nativeWidth = videoElement.videoWidth;
      const nativeHeight = videoElement.videoHeight;

      if (!nativeWidth || !nativeHeight || !displayWidth || !displayHeight) {
        return;
      }

      const containerAspectRatio = displayWidth / displayHeight;
      const videoAspectRatio = nativeWidth / nativeHeight;

      let scaleFactor: number;
      if (containerAspectRatio > videoAspectRatio) {
        scaleFactor = displayHeight / nativeHeight;
      } else {
        scaleFactor = displayWidth / nativeWidth;
      }

      const finalWidth = Math.round(nativeWidth * scaleFactor);
      const finalHeight = Math.round(nativeHeight * scaleFactor);
      const prevWidth = canvasFabric.getWidth();
      const scale = prevWidth > 0 ? finalWidth / prevWidth : scaleFactor;

      canvasFabric.setDimensions({ width: finalWidth, height: finalHeight });
      this.rescaleCanvas(scale);
    }
  }

  private rescaleCanvas(scale: number): void {
    const { canvasFabric } = this.player;
    canvasFabric.getObjects().forEach((object: any) => {
      object.set('left', object.left * scale);
      object.set('top', object.top * scale);
      object.set('scaleX', object.scaleX * scale);
      object.set('scaleY', object.scaleY * scale);
      object.set('strokeWidth', object.strokeWidth / scale);
      object.setCoords();
    });
    canvasFabric.renderAll();
    setTimeout(() => {
      EmitterService.get('RESIZE_OBSERVER_STATE_UPDATE').emit({ instanceId: this.instanceId });
    }, 500);
  }

  toggleAnnotationMode(): void {
    this.active = !this.active;
    this.player.toggleClass('vac-active');
    this.canvasContainer.toggleClass('vac-hidden');
    $(document.querySelector('.annotation-bar-container') as HTMLElement).toggleClass('vac-hidden');

    const imgElement = document.querySelector('.vac-toggle-annot-display img') as HTMLImageElement;
    if (imgElement) {
      $(imgElement).toggleClass('active');
      if (imgElement.classList.contains('active')) {
        imgElement.src = '../../../../../assets/img/annotations/toggleDisplayIconDisabled.png';
      } else {
        imgElement.src = '../../../../../assets/img/annotations/toggleDisplayIconEnabled.png';
      }
    }
  }

  changeTimeFormat(format: 'standard' | 'frames'): void {
    if (format === 'standard') {
      videojs.resetFormatTime();
      this.player.controlBar.currentTimeDisplay.updateContent();
      this.player.controlBar.durationDisplay.updateContent();
    } else if (format === 'frames') {
      videojs.setFormatTime((sec: number) => {
        const videoFrameRate = this.videoMetadata.avgFrameRate
          .split('/')
          .map((x: string) => +x)
          .reduce((a: number, b: number) => a / b);
        const frame = Math.floor(sec * videoFrameRate);
        return frame;
      });
      this.player.controlBar.durationDisplay.updateContent();
    }
    this.videoDisplay = format;
    onTimeFormatChangePlugin.emit({ act: 'timeFormatChanged', format: format });
  }

  saveAnnotation(annotationComment: string, aiParams: AiOptions, isGeneralComment: boolean = false) {
    const shape = this.drawingHandler.getCurrentShapes();
    const resolution = {
      width: this.player.canvasFabric.getWidth(),
      height: this.player.canvasFabric.getHeight(),
    };
    const fallbackRange = { start: null, end: null };
    const markerRange = this.marker?.range ?? fallbackRange;
    const annotationParams: AnnotationParams = {
      shape: JSON.stringify(shape),
      annotationComment: annotationComment,
      start: markerRange.start,
      end: markerRange.end,
      isGeneralComment: isGeneralComment,
      privateConditionKey: null,
      agentVersionId: aiParams.agentVersionId ?? null,
      mediaQuality: aiParams.mediaQuality ?? null,
      resolution: resolution,
      // privateConditionKey: this.plugin.primordial.privateCondition
    };
    this.annotationPlugin.fire('saveNewAnnotation', { annotationParams: annotationParams });
  }

  saveEditedAnnotation(annotationComment: string, aiParams: AiOptions) {
    const shape = this.drawingHandler.getCurrentShapes();
    const annotationParams: AnnotationParams = {
      shape: JSON.stringify(shape),
      annotationComment: annotationComment,
      start: this.marker?.range.start ?? null,
      end: this.marker?.range.end ?? null,
      isGeneralComment: false,
      privateConditionKey: null,
      agentVersionId: aiParams.agentVersionId ?? null,
      mediaQuality: aiParams.mediaQuality ?? null,
      // privateConditionKey: this.plugin.primordial.privateCondition
    };
    this.annotationPlugin.fire('saveEditedAnnotation', { annotationParams: annotationParams });
  }

  teardown(): void {
    this.destroy();
    this.annotationPlugin.fire('dispose');
  }

  destroy(): void {
    this.stopAnimationLoop();
    if (this.drawingHandler) {
      this.drawingHandler.teardown();
      this.drawingHandler = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.player) {
      this.player.off('play');
      this.player.off('pause');
      this.player.off('ended');
      this.player.off('seeked');
    }

    $(document).off('click', '#toggleDisplayIcon');

    const controlTimeEls = document.querySelectorAll('.vjs-time-control, .vjs-duration-display .format-types');
    controlTimeEls.forEach((el) => {
      $(el as HTMLElement).off('click');
    });
    const stdControlBarEl = document.querySelector('.vjs-duration-display .std-format');
    const frameControlBarEl = document.querySelector('.vjs-duration-display .frame-format');
    $(stdControlBarEl as HTMLElement).off('click');
    $(frameControlBarEl as HTMLElement).off('click');

    this.unregisterAddingAnnotationRangeListeners();
    if (this.marker) {
      this.marker.teardown();
      this.marker = null;
    }

    // this.cleanupAnnotationPluginListeners();
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  private cleanupAnnotationPluginListeners(): void {
    if (!this.annotationPlugin?.unregisterListener) {
      return;
    }

    const listenerTypes = [
      'enteredAddingAnnotation',
      'exitingAddingAnnotation',
      'enteredEditingAnnotation',
      'exitingEditingAnnotation',
      'annotationClickedIn',
      'annotationClickedOut',
      'annotationHoveredIn',
      'annotationHoveredOut',
      'annotationDeleted',
      'addStartTimeRange',
      'addEndTimeRange',
      'removeStartTimeRange',
      'removeEndTimeRange',
      'startRangeUpdate',
      'endRangeUpdate',
      'enableAnnotationDrawing',
      'disableAnnotationDrawing',
    ];

    listenerTypes.forEach((listenerType) => {
      this.annotationPlugin.unregisterListener(listenerType);
    });
  }

  registerListeners() {
    this.annotationPlugin.registerListener('enteredAddingAnnotation', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }

      if (!this.player.paused()) {
        this.player.pause();
      }

      const progressControl = this.getProgressControlEl();
      if (progressControl) {
        progressControl.classList.add('annotation-mode-active');
      }
      this.unregisterAddingAnnotationRangeListeners();
      this.registerAddingAnnotationRangeListeners();
    });

    this.annotationPlugin.registerListener('exitingAddingAnnotation', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }

      const progressControl = this.getProgressControlEl();
      if (progressControl) {
        progressControl.classList.remove('annotation-mode-active');
      }
      this.drawingHandler.resetMode(false, false);
      if (this.marker) {
        this.marker.teardown();
        this.marker = null;
      }
      this.unregisterAddingAnnotationRangeListeners();
    });

    this.annotationPlugin.registerListener('enteredEditingAnnotation', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      const range = event?.detail?.annotation?.range ?? { start: null, end: null };
      const rawStart = range.start ?? null;
      const rawEnd = range.end ?? null;
      const normalizedStart = typeof rawStart === 'number' ? rawStart : null;
      const normalizedEnd = typeof rawEnd === 'number' ? rawEnd : null;
      const seekTarget = normalizedStart ?? normalizedEnd;
      const upperBound =
        seekTarget !== null && normalizedEnd !== null ? Math.max(seekTarget, normalizedEnd) : seekTarget;

      if (seekTarget !== null) {
        const current = this.player.currentTime();
        if (current < seekTarget || (upperBound !== null && current > upperBound)) {
          this.player.currentTime(seekTarget);
        }
      }
      if (!this.player.paused()) {
        this.player.pause();
      }
      setTimeout(() => {
        if (this.drawingHandler) {
          this.drawingHandler.changeActiveTool('rectangle', false);
        }

        const progressControl = this.getProgressControlEl();
        if (progressControl) {
          progressControl.classList.add('annotation-mode-active');
        }

        const targetElement = this.getProgressControlEl() as HTMLElement;
        const markerStart = normalizedStart ?? normalizedEnd;
        const markerEnd = normalizedEnd;

        if (this.marker) {
          this.marker.teardown();
          this.marker = null;
        }

        if (targetElement && markerStart !== null) {
          this.marker = new BaseDraggableMarkerComponent({
            range: {
              start: markerStart,
              end: markerEnd,
            },
            instanceId: this.instanceId,
            annotationPlugin: this.annotationPlugin,
            player: this.player,
            targetElement,
          });
        }

        this.marker.fireAnnotationDataChanged();
        this.drawingHandler.resetMode(false, false);
        this.visibleAnnotationIds.clear();
        this.drawingHandler.addAnnotationDrawings(
          event.detail.annotation.id,
          event.detail.annotation.shape,
          false,
          false,
          event.detail.annotation.resolution
        );
        this.annotationPlugin.fire('enableAnnotationDrawing', {
          instanceId: this.instanceId,
          itemId: this.itemId,
        });
        EmitterService.get('videoTimeChange').emit({
          startTime: normalizedStart,
          endTime: normalizedEnd,
          instanceId: this.instanceId,
        });
        this.unregisterAddingAnnotationRangeListeners();
        this.registerAddingAnnotationRangeListeners();
      }, 200);
    });

    this.annotationPlugin.registerListener('exitingEditingAnnotation', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }

      const progressControl = this.getProgressControlEl();
      if (progressControl) {
        progressControl.classList.remove('annotation-mode-active');
      }
      this.drawingHandler.resetMode(false, false);
      if (this.marker) {
        this.marker.teardown();
        this.marker = null;
      }
      this.unregisterAddingAnnotationRangeListeners();
    });

    this.annotationPlugin.registerListener('annotationDeleted', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      this.drawingHandler.removeAnnotationDrawings(event.detail.id);
    });

    this.annotationPlugin.registerListener('annotationCreated', (event: any) => {
      if (!this.isAnnotatingOrEditing) this.drawingHandler.resetMode(true, false);
    });

    this.annotationPlugin.registerListener('annotationEdited', (event: any) => {
      if (!this.isAnnotatingOrEditing) this.drawingHandler.resetMode(true, false);
    });

    this.annotationPlugin.registerListener('enableAnnotationDrawing', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      if (this.player && !this.player.paused()) {
        this.player.pause();
      }
      this.drawingHandler?.enableDrawingInteractions();
    });

    this.annotationPlugin.registerListener('disableAnnotationDrawing', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      this.drawingHandler?.disableDrawingInteractions();
    });

    onAnnotationMarkerSelectedActiveHover.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      const annotationId = event?.annotation?.id;
      if (annotationId && this.drawingHandler) {
        this.drawingHandler.highlightAnnotation(annotationId);
      }
    });

    onAnnotationMarkerSelectedInActiveHover.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      const annotationId = event?.annotation?.id;
      if (annotationId && this.drawingHandler) {
        this.drawingHandler.unhighlightAnnotation(annotationId);
      }
    });

    this.annotationPlugin.registerListener('annotationClickedIn', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      const annotationId = this.getAnnotationIdFromEvent(event);
      if (annotationId) {
        this.drawingHandler.disableAnnotationHover();
        this.handleOpenAnnotation(event.detail);
        this.drawingHandler.highlightAnnotation(annotationId, true);
      }
      onAnnotationClickOpenedV2.emit({
        annotation: event.detail.annotation,
        itemId: this.itemId,
        instanceId: this.instanceId,
      });
    });

    this.annotationPlugin.registerListener('annotationClickedOut', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      const annotationId = this.getAnnotationIdFromEvent(event);
      if (annotationId) {
        this.drawingHandler.unhighlightAnnotation(annotationId, true);
        this.drawingHandler.enableAnnotationHover();
      }
      onAnnotationClickClosedV2.emit({
        annotation: null,
        itemId: this.itemId,
        instanceId: this.instanceId,
      });
    });

    // this.annotationPlugin.registerListener('onStateChanged', (event: any) => {
    //   onAnnotationsStateChanged.emit({});
    //   this.buildIntervalTree();
    // });

    this.annotationPlugin.registerListener('annotationMessagesUpdated', (event: any) => {
      onAnnotationsStateChanged.emit({});
      this.buildIntervalTree();
      this.syncFabricCanvasDrawings(this.searchIntervalTree(this.player.currentTime()));
    });

    onAnnotationDirtyStateChanged.pipe(takeUntil(this.ngUnsubscribe)).subscribe(({ dirty, instanceId }) => {
      if (instanceId !== this.instanceId) {
        return;
      }
      this.hasUnsavedDrawingState = !!dirty;
    });

    EmitterService.get('changeActiveTool')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: any) => {
        if (!this.isEventForThisInstance(event)) {
          return;
        }
        this.drawingHandler.changeActiveTool(event.tool, false);
      });

    EmitterService.get('changeColor')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: any) => {
        if (!this.isEventForThisInstance(event)) {
          return;
        }
        this.drawingHandler.changeColor(event.color);
      });

    EmitterService.get('startRangeUpdate')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: any) => {
        if (event.instanceId !== this.instanceId) {
          return;
        }
        const currentTime = this.player?.currentTime?.() ?? 0;
        const seconds = event?.seconds ?? currentTime;
        console.log('updating start time', seconds);
        if (typeof seconds === 'number' && seconds !== 0) {
          this.marker?.updateStart(seconds);
        }
      });

    EmitterService.get('endRangeUpdate')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: any) => {
        if (event.instanceId !== this.instanceId) {
          return;
        }
        const currentTime = this.player?.currentTime?.() ?? 0;
        const seconds = event?.seconds ?? currentTime;
        console.log('updating end time', seconds);
        if (typeof seconds === 'number' && seconds !== 0) {
          this.marker?.updateEnd(seconds);
        }
      });

    onOpenAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe((args: any) => {
      this.handleOpenAnnotation(args);
    });

    this.annotationPlugin.registerListener('channelSubscribed', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      this.drawingHandler?.resetMode(false);
      // this.setBounds();
      this.syncFabricCanvasDrawings(this.searchIntervalTree(this.player.currentTime()));
      this.channelSubscribed = true;
    });

    this.annotationPlugin.registerListener('selectChannel', (event: any) => {
      if (!this.isEventForThisInstance(event)) {
        return;
      }
      this.channelSubscribed = false;
    });
  }

  private registerAddingAnnotationRangeListeners(): void {
    if (!this.annotationPlugin?.registerListener || this.markerRangeListenersRegistered) {
      return;
    }

    this.annotationPlugin.registerListener('addStartTimeRange', this.handleAddStartTimeRange);
    this.annotationPlugin.registerListener('removeStartTimeRange', this.handleRemoveStartTimeRange);
    this.annotationPlugin.registerListener('addEndTimeRange', this.handleAddEndTimeRange);
    this.annotationPlugin.registerListener('removeEndTimeRange', this.handleRemoveEndTimeRange);
    this.markerRangeListenersRegistered = true;
  }

  private unregisterAddingAnnotationRangeListeners(): void {
    if (!this.annotationPlugin?.unregisterListener) {
      return;
    }

    const listenerTypes = ['addStartTimeRange', 'removeStartTimeRange', 'addEndTimeRange', 'removeEndTimeRange'];
    listenerTypes.forEach((listenerType) => {
      this.annotationPlugin.unregisterListener(listenerType);
    });
    this.markerRangeListenersRegistered = false;
  }

  private readonly handleAddStartTimeRange = (event: any): void => {
    if (!this.isEventForThisInstance(event)) {
      return;
    }

    this.pauseIfPlaying();
    const startTime = this.player.currentTime();

    if (this.marker) {
      this.marker.teardown();
      this.marker = null;
    }

    const progressControl = this.getProgressControlEl();
    if (progressControl) {
      progressControl.classList.add('annotation-mode-active');
    }

    const targetElement = this.getProgressControlEl() as HTMLElement;
    if (!targetElement) {
      EmitterService.get('videoTimeChange').emit({
        startTime,
        endTime: null,
        instanceId: this.instanceId,
      });
      return;
    }

    this.marker = new BaseDraggableMarkerComponent({
      range: { start: startTime, end: null },
      instanceId: this.instanceId,
      annotationPlugin: this.annotationPlugin,
      player: this.player,
      targetElement,
    });
    this.marker.fireAnnotationDataChanged();
    EmitterService.get('videoTimeChange').emit({
      startTime,
      endTime: null,
      instanceId: this.instanceId,
    });
  };

  private readonly handleAddEndTimeRange = (event: any): void => {
    if (!this.isEventForThisInstance(event)) {
      return;
    }

    this.pauseIfPlaying();
    const currentTime = this.player.currentTime();
    const normalizedStart = this.marker?.range.start ?? currentTime;
    const normalizedEnd = normalizedStart + 0.05;

    if (!this.marker) {
      const targetElement = this.getProgressControlEl() as HTMLElement;
      if (!targetElement) {
        EmitterService.get('videoTimeChange').emit({
          startTime: normalizedStart,
          endTime: normalizedEnd,
          instanceId: this.instanceId,
        });
        return;
      }
      this.marker = new BaseDraggableMarkerComponent({
        range: { start: normalizedStart, end: normalizedEnd },
        instanceId: this.instanceId,
        annotationPlugin: this.annotationPlugin,
        player: this.player,
        targetElement,
      });
    } else {
      this.marker.setStartTime(normalizedStart);
      this.marker.setEndTime(normalizedEnd);
    }

    this.marker.fireAnnotationDataChanged();
    EmitterService.get('videoTimeChange').emit({
      startTime: normalizedStart,
      endTime: normalizedEnd,
      instanceId: this.instanceId,
    });
  };

  private readonly handleRemoveStartTimeRange = (event: any): void => {
    if (!this.isEventForThisInstance(event)) {
      return;
    }

    this.pauseIfPlaying();
    if (this.marker) {
      this.marker.teardown();
      this.marker = null;
    }

    const progressControl = this.getProgressControlEl();
    if (progressControl) {
      progressControl.classList.remove('annotation-mode-active');
    }

    EmitterService.get('videoTimeChange').emit({
      startTime: null,
      endTime: null,
      instanceId: this.instanceId,
    });
  };

  private readonly handleRemoveEndTimeRange = (event: any): void => {
    if (!this.isEventForThisInstance(event)) {
      return;
    }

    this.pauseIfPlaying();
    if (this.marker) {
      this.marker.setEndTime(null);
    }

    const startTime = this.marker?.range.start ?? null;

    EmitterService.get('videoTimeChange').emit({
      startTime,
      endTime: null,
      instanceId: this.instanceId,
    });
  };

  private handleOpenAnnotation(args: any): void {
    if (args.instanceId !== this.instanceId || args.itemId !== this.itemId) {
      return;
    }

    if (this.player && !this.player.paused()) {
      this.player.pause();
    }

    if (args.annotation?.range) {
      const { annotation } = args;
      const { start: startTime, end: endTime } = annotation.range;
      const current = this.player?.currentTime?.() ?? 0;
      const end = typeof endTime === 'number' && !Number.isNaN(endTime) ? endTime : startTime;
      const alreadyInRange = current >= startTime && current <= end;
      if (!alreadyInRange) {
        this.player.currentTime(startTime);
      }

      // if (annotation.id && this.drawingHandler) {
      //   const foundAnnotation = this.drawingHandler.findAnnotation(annotation.id);
      //   if (foundAnnotation) {
      //     this.drawingHandler.clickedAnnotation = foundAnnotation;
      //   }
      // }
    }
  }

  private getAnnotationIdFromEvent(event: any): string | null {
    const id = event?.detail?.annotation?.id ?? event?.detail?.id ?? event?.annotation?.id ?? event?.id ?? null;
    return typeof id === 'string' ? id : null;
  }

  private getProgressControlEl(): Element | null {
    return this.player?.el()?.querySelector?.('.vjs-progress-control') ?? null;
  }

  private pauseIfPlaying(): void {
    try {
      if (this.player && typeof this.player.paused === 'function' && !this.player.paused()) {
        this.player.pause();
      }
    } catch (_) {}
  }

  private isEventForThisInstance(event: any): boolean {
    if (event?.instanceId) {
      return event.instanceId === this.instanceId;
    }

    if (event?.itemId) {
      return event.itemId === this.itemId;
    }

    if (event?.detail) {
      if (event.detail.instanceId) {
        return event.detail.instanceId === this.instanceId;
      }
      if (event.detail.itemId) {
        return event.detail.itemId === this.itemId;
      }
    }

    return true;
  }

  deleteCommentAnnotation(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.item.id) {
      return;
    }
    this.annotationPlugin.fire('destroyComment', { id: args.comment.id, annotation: args.annotation });
  }
}
